import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { PII_MASK_CLASS } from "@/lib/analytics";
import { memo, useMemo, useState, type FormEvent } from "react";
import * as z from "zod";

const emailSchema = z.email();

interface SupplierMatchEmailFormProps {
  isSubmitting?: boolean;
  error?: string | null;
  onSubmit: (email: string) => void;
  /**
   * Starting value of the field. Left empty for the Case B alternate email,
   * which a salon always types fresh. Only the trade-account claim passes one,
   * and only ever the login email (KAN-322, amending ADR-0005).
   */
  initialEmail?: string;
  /**
   * Emails the salon already used (see `collectSupplierMatchEmails`), offered
   * as quick picks. A suggestion never fills the field by itself - the
   * default stays empty - and the field stays free text.
   */
  suggestions?: readonly string[];
  /**
   * Emails never to suggest (case-insensitive): the one this field replaces,
   * since picking it would change nothing.
   */
  excludeEmails?: readonly string[];
  /** Accessible name of the field. */
  inputLabel?: string;
  /** Submit button text; `submittingLabel` replaces it while in flight. */
  submitLabel?: string;
  submittingLabel?: string;
}

/**
 * The "type an email" form: the Case B alternate lookup email (ADR-0005) and
 * the trade-account claim email (KAN-322). By default it is never pre-filled
 * from any existing email - a salon types a fresh address, even if a verified
 * one already exists for this or another seller. The claim flow is the one
 * exception and opts in through `initialEmail`. Previously used emails are
 * offered as suggestions (a combobox), masked in session replay (ADR-0006).
 */
export const SupplierMatchEmailForm = memo(function SupplierMatchEmailForm({
  isSubmitting = false,
  error,
  onSubmit,
  initialEmail = "",
  suggestions,
  excludeEmails,
  inputLabel = "Alternate email",
  submitLabel = "Send confirmation email",
  submittingLabel = "Sending…"
}: SupplierMatchEmailFormProps) {
  // `null` until the salon types: the field follows `initialEmail` (which can
  // arrive late, e.g. the profile still loading) and never overwrites typing.
  const [typed, setTyped] = useState<string | null>(null);
  const email = typed ?? initialEmail;

  const options = useMemo(() => {
    const excluded = new Set(
      (excludeEmails ?? []).map((excludedEmail) =>
        excludedEmail.trim().toLowerCase()
      )
    );
    return (suggestions ?? []).filter(
      (suggestion) => !excluded.has(suggestion.trim().toLowerCase())
    );
  }, [suggestions, excludeEmails]);

  const trimmed = email.trim();
  const isValid = emailSchema.safeParse(trimmed).success;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (isValid && !isSubmitting) onSubmit(trimmed);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <Combobox
        // `inputMode="email"` rather than `type="email"`, for consistency with
        // the other email inputs; `isValid` above gates the submit.
        // `autoComplete="off"` is deliberate (api ADR-0005): the suggestions
        // are the salon's own Supplier Match emails, not the browser's.
        inputMode="email"
        value={email}
        onValueChange={setTyped}
        options={options}
        listLabel="Emails you have used"
        optionClassName={PII_MASK_CLASS}
        placeholder="name@example.com"
        aria-label={inputLabel}
        autoComplete="off"
      />
      {error ? (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : null}
      <Button
        type="submit"
        size="sm"
        variant="outline"
        disabled={!isValid || isSubmitting}
      >
        {isSubmitting ? submittingLabel : submitLabel}
      </Button>
    </form>
  );
});
